import { DONE_VISIBLE_MS, WORKING_VISIBLE_MS, isAttentionState } from "@/components/toasts/toast-status";
import { sameActivityLabel } from "@/lib/activity-label";
import type { FrameApplyContext } from "@/lib/companion-frame-reducer";
import { findWorkToastKeyByAwaitingInputId, type WorkToastActivityEntry, type WorkToastItem, type WorkToastState } from "@/stores/work-toast-store";
import { hasComputerUseEvidence } from "./work-toast-computer-pin";

type WorkToastStoreContext = Pick<FrameApplyContext, "workToastStore">;

/** A non-empty string field, else null (identity payloads send "" for unknown ids). */
export function payloadString(data: Record<string, unknown> | undefined, key: string): string | null {
  const value = data?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

/** Enters a state without announcing it: Working and Done start with their window elapsed. */
export function silentStateChangedAt(state: WorkToastState, now: number): number {
  if (state === "working") return now - WORKING_VISIBLE_MS;
  return state === "done" ? now - DONE_VISIBLE_MS : now;
}

export function workToastTaskTitle(ctx: FrameApplyContext, taskId: string | null): string | null {
  if (!taskId) return null;
  return ctx.tasksStore.getState().tasks.find((task) => task.id === taskId)?.title || null;
}

export function newestWorkToast(
  items: Record<string, WorkToastItem>,
  match: (item: WorkToastItem) => boolean,
): WorkToastItem | null {
  let newest: WorkToastItem | null = null;
  for (const item of Object.values(items)) {
    if (match(item) && (!newest || item.stateChangedAt > newest.stateChangedAt)) newest = item;
  }
  return newest;
}

/** Group key for "same task" collapsing: a follow-up dispatches a new
 *  execution (new item key) for the same task, so the rail/stack must show
 *  only the newest item per group instead of stacking old Done + new Working. */
function workToastTaskGroupKey(item: WorkToastItem): string | null {
  // A receipt has its own lifecycle: dismissing it must not dismiss the run.
  if (item.answerDelivery) return null;
  if (item.source !== "agent" && item.source !== "native_worker") return null;
  return item.taskId ?? item.conversationId ?? item.nativeSubagentId;
}

/** A newer status cannot resolve a question, approval, saved reply — or a
 *  failure. Keep every attention item plus undismissed failures in a task
 *  group; otherwise keep its newest status. Failures must stay visible
 *  because the tray icon counts them toward attention: a counted-but-hidden
 *  failure leaves attention stuck with nothing to dismiss. */
export function dedupWorkToastsByTask(items: WorkToastItem[]): WorkToastItem[] {
  const newestByGroup = new Map<string, WorkToastItem>();
  const attentionGroups = new Set<string>();
  for (const item of items) {
    const group = workToastTaskGroupKey(item);
    if (!group) continue;
    if (isStickyUnresolved(item)) attentionGroups.add(group);
    const current = newestByGroup.get(group);
    if (!current || item.stateChangedAt > current.stateChangedAt) newestByGroup.set(group, item);
  }
  if (newestByGroup.size === 0) return items;
  const newestKeys = new Set([...newestByGroup.values()].map((item) => item.key));
  return items.filter((item) => {
    const group = workToastTaskGroupKey(item);
    return !group || (attentionGroups.has(group) ? isStickyUnresolved(item) : newestKeys.has(item.key));
  });
}

/** Undismissed items the tray icon counts toward attention: questions,
 *  approvals, blocked — and failures. The tray's workAttentionReason counts
 *  with this rule, so every counted pill stays visible and dismissible. */
export function isStickyUnresolved(item: Pick<WorkToastItem, "dismissed" | "state">): boolean {
  return !item.dismissed && (isAttentionState(item.state) || item.state === "failed");
}

type NewWorkToastItem = Pick<WorkToastItem, "key" | "source" | "tracked" | "state" | "agentName"> &
  Partial<Omit<WorkToastItem, "stateChangedAt">>;

export function createWorkToastItem(fields: NewWorkToastItem, stateChangedAt: number): WorkToastItem {
  return {
    stateBeforeBlocked: null, projectId: null,
    conversationId: null, taskId: null, nativeSubagentId: null, apps: [], codingProvider: null,
    openTargets: [], detail: "", resultText: null,
    question: null, approval: null, dismissed: false, ...fields, stateChangedAt,
  };
}

/** Same-state moves only patch fields, so a re-applied frame never clears a
 *  dismissal; a genuine state change does, which re-shows a pill the user
 *  dismissed in its previous state. A blocked item records the new state for
 *  when it unblocks. */
export function moveWorkToast(
  ctx: WorkToastStoreContext, item: WorkToastItem, state: WorkToastState, stateChangedAt: number,
  patch: Partial<Omit<WorkToastItem, "key">> = {},
): void {
  const store = ctx.workToastStore.getState();
  if (item.state === state) store.patchItem(item.key, patch);
  else if (item.state === "blocked") store.patchItem(item.key, { ...patch, stateBeforeBlocked: state });
  else store.patchItem(item.key, { ...patch, state, stateChangedAt, dismissed: item.dismissed && hasComputerUseEvidence(item) });
}

/** A question is over (resolved, answered here, or no longer pending): an
 *  untracked item leaves, a tracked one returns to hidden Working. */
export function resolveWorkToastQuestion(ctx: WorkToastStoreContext, awaitingInputId: string, now: number): void {
  const store = ctx.workToastStore.getState();
  const key = findWorkToastKeyByAwaitingInputId(store.items, awaitingInputId);
  const item = key ? store.items[key] : undefined;
  if (!item) return;
  if (!item.tracked) store.removeItem(item.key);
  else moveWorkToast(ctx, item, "working", silentStateChangedAt("working", now), { question: null });
}

export const WORK_TOAST_ACTIVITY_CAP = 3;

function sameActivity(a: WorkToastActivityEntry, b: WorkToastActivityEntry): boolean {
  return a.id === b.id && a.kind === b.kind && a.app === b.app && a.action === b.action
    && sameActivityLabel(a.actionLabel, b.actionLabel) && a.text === b.text && a.textFull === b.textFull;
}

function mergeActivityParts(existing: WorkToastActivityEntry, entry: WorkToastActivityEntry): WorkToastActivityEntry {
  const oneComputer = (existing.kind === "computer") !== (entry.kind === "computer");
  const lead = oneComputer && existing.kind === "computer" ? existing : entry;
  const trail = lead === existing ? entry : existing;
  return {
    id: entry.id,
    kind: lead.kind === "computer" ? "computer" : entry.kind,
    app: lead.app ?? trail.app,
    action: lead.action ?? trail.action,
    actionLabel: lead.action !== undefined ? lead.actionLabel : trail.actionLabel,
    text: lead.text ?? trail.text,
    textFull: lead.textFull ?? trail.textFull,
    updatedAt: Math.max(existing.updatedAt, entry.updatedAt),
  };
}

/** Merge a frame's structured parts and move its event to the newest slot. */
export function mergeWorkToastActivity(
  ring: WorkToastActivityEntry[] | undefined,
  entry: WorkToastActivityEntry,
): WorkToastActivityEntry[] {
  const current = ring ?? [];
  const existing = current.find((candidate) => candidate.id === entry.id);
  const merged = existing ? mergeActivityParts(existing, entry) : entry;
  return [...current.filter((candidate) => candidate.id !== entry.id), merged].slice(-WORK_TOAST_ACTIVITY_CAP);
}

/** Union of two rings after adoption or execution binding. */
export function mergeWorkToastActivityRings(
  a: WorkToastActivityEntry[] | undefined,
  b: WorkToastActivityEntry[] | undefined,
): WorkToastActivityEntry[] {
  const byId = new Map<string, WorkToastActivityEntry>();
  for (const entry of [...(a ?? []), ...(b ?? [])]) {
    const previous = byId.get(entry.id);
    if (!previous || entry.updatedAt >= previous.updatedAt) byId.set(entry.id, entry);
  }
  return [...byId.values()].sort((x, y) => x.updatedAt - y.updatedAt).slice(-WORK_TOAST_ACTIVITY_CAP);
}

/** Record a changed live event and refresh the working visibility window. */
export function pushWorkToastActivity(ctx: WorkToastStoreContext, key: string, entry: WorkToastActivityEntry): boolean {
  const store = ctx.workToastStore.getState();
  const item = store.items[key];
  if (!item) return false;
  const previous = item.activity ?? [];
  const activity = mergeWorkToastActivity(previous, entry);
  if (previous.length === activity.length && previous.every((e, i) => sameActivity(e, activity[i]!))) return false;
  store.patchItem(key, {
    activity,
    stateChangedAt: entry.updatedAt,
    ...(item.resultText ? { resultSuperseded: true } : {}),
  });
  return true;
}
